import { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Loader2, Plus, Trash2, Save, GripVertical, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_CHOICES = ['shield', 'wifi', 'coffee', 'clock', 'sofa', 'map-pin', 'sparkles', 'home'];

/**
 * A single, scrollable sheet with every field on the landing page grouped
 * into collapsible sections. All edits happen locally; hitting Save PUTs the
 * whole doc to /api/landing/config.
 */
export function LandingEditor({ open, onClose, cfg, adminPass, onSaved }) {
  const [draft, setDraft] = useState(cfg);
  const [inventory, setInventory] = useState([]);
  const [photoLibrary, setPhotoLibrary] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);

  useEffect(() => {
    setDraft(cfg);
  }, [cfg]);

  // Load properties/units for the featured-homes editor
  useEffect(() => {
    if (!open || !adminPass) return;
    fetch(`${API}/api/landing/editor/inventory?password=${encodeURIComponent(adminPass)}`)
      .then(r => r.json())
      .then(d => setInventory(d.properties || []))
      .catch(() => setInventory([]));
    fetch(`${API}/api/landing/editor/photos?password=${encodeURIComponent(adminPass)}`)
      .then(r => r.json())
      .then(d => setPhotoLibrary(d.photos || []))
      .catch(() => setPhotoLibrary([]));
  }, [open, adminPass]);

  const patch = (key, value) => setDraft(d => ({ ...d, [key]: value }));
  const patchNested = (key, sub, value) => setDraft(d => ({ ...d, [key]: { ...(d[key] || {}), [sub]: value } }));

  const patchArrayItem = (key, idx, sub, value) => setDraft(d => {
    const arr = [...(d[key] || [])];
    arr[idx] = { ...arr[idx], [sub]: value };
    return { ...d, [key]: arr };
  });
  const removeArrayItem = (key, idx) => setDraft(d => {
    const arr = [...(d[key] || [])];
    arr.splice(idx, 1);
    return { ...d, [key]: arr };
  });
  const addArrayItem = (key, template) => setDraft(d => ({ ...d, [key]: [...(d[key] || []), template] }));
  const moveArrayItem = (key, idx, direction) => setDraft(d => {
    const arr = [...(d[key] || [])];
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= arr.length) return d;
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    return { ...d, [key]: arr };
  });

  const patchHomesSettings = (patchObj) => setDraft(d => ({
    ...d,
    homes_settings: { ...(d.homes_settings || {}), ...patchObj },
  }));

  const save = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/landing/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...draft, password: adminPass }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Save failed');
      const next = await res.json();
      toast.success('Landing page updated');
      onSaved?.(next);
      onClose?.();
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-[560px] p-0 flex flex-col gap-0" data-testid="landing-editor">
        <SheetHeader className="px-5 py-4 border-b flex-shrink-0">
          <SheetTitle>Edit Landing Page</SheetTitle>
          <SheetDescription>Everything you type here updates the live landing page after Save.</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {/* ─── Nav / Brand ─── */}
          <Section title="Brand & Nav">
            <Field label="Brand name">
              <Input value={draft?.nav?.brand_name || ''} onChange={e => patchNested('nav', 'brand_name', e.target.value)} data-testid="editor-brand-name" />
            </Field>
          </Section>

          {/* ─── Hero ─── */}
          <Section title="Hero">
            <Field label="Badge (small text above title)">
              <Input value={draft?.hero?.badge || ''} onChange={e => patchNested('hero', 'badge', e.target.value)} data-testid="editor-hero-badge" />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Title — top line">
                <Input value={draft?.hero?.title_top || ''} onChange={e => patchNested('hero', 'title_top', e.target.value)} data-testid="editor-hero-title-top" />
              </Field>
              <Field label="Title — bottom (italic)">
                <Input value={draft?.hero?.title_bottom || ''} onChange={e => patchNested('hero', 'title_bottom', e.target.value)} data-testid="editor-hero-title-bottom" />
              </Field>
            </div>
            <Field label="Subtitle">
              <Textarea rows={3} value={draft?.hero?.subtitle || ''} onChange={e => patchNested('hero', 'subtitle', e.target.value)} data-testid="editor-hero-subtitle" />
            </Field>
            <Field label="Book Now button label">
              <Input value={draft?.hero?.book_now_label || ''} onChange={e => patchNested('hero', 'book_now_label', e.target.value)} />
            </Field>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Hero slideshow images</Label>
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1"
                  onClick={() => setShowPhotoPicker(true)}
                  data-testid="editor-open-photo-picker"
                >
                  <Plus className="h-3 w-3" /> Pick from listings
                </Button>
              </div>
              <div className="space-y-1.5">
                {(draft?.hero?.images || []).map((url, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <img src={url && !url.startsWith('http') ? `${API}${url}` : url} alt="" className="h-10 w-14 object-cover rounded-md border border-[#D7E0D5]" />
                    <Input
                      value={url}
                      onChange={e => setDraft(d => {
                        const arr = [...(d.hero?.images || [])];
                        arr[i] = e.target.value;
                        return { ...d, hero: { ...d.hero, images: arr } };
                      })}
                      className="flex-1 text-xs"
                      placeholder="Image URL"
                    />
                    <div className="flex gap-0.5">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDraft(d => {
                        const arr = [...(d.hero?.images || [])];
                        if (i <= 0) return d;
                        [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
                        return { ...d, hero: { ...d.hero, images: arr } };
                      })}><span className="text-xs">↑</span></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDraft(d => {
                        const arr = [...(d.hero?.images || [])];
                        if (i >= arr.length - 1) return d;
                        [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
                        return { ...d, hero: { ...d.hero, images: arr } };
                      })}><span className="text-xs">↓</span></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => setDraft(d => {
                        const arr = [...(d.hero?.images || [])];
                        arr.splice(i, 1);
                        return { ...d, hero: { ...d.hero, images: arr } };
                      })}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="gap-1 text-xs"
                    onClick={() => setDraft(d => ({ ...d, hero: { ...d.hero, images: [...(d.hero?.images || []), ''] } }))}
                  >
                    <Plus className="h-3 w-3" /> Add URL manually
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground pt-1">
                  Leave empty to use the default stock photos. Order determines slideshow order (top → first).
                </p>
              </div>
            </div>

            <div className="border-t pt-3">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Trust badges (under the CTAs)</Label>
              <div className="mt-2 space-y-2">
                {(draft?.hero?.trust_badges || []).map((tb, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <IconSelect value={tb.icon} onChange={(v) => setDraft(d => {
                      const arr = [...(d.hero?.trust_badges || [])];
                      arr[i] = { ...arr[i], icon: v };
                      return { ...d, hero: { ...d.hero, trust_badges: arr } };
                    })} />
                    <Input value={tb.label || ''} onChange={e => setDraft(d => {
                      const arr = [...(d.hero?.trust_badges || [])];
                      arr[i] = { ...arr[i], label: e.target.value };
                      return { ...d, hero: { ...d.hero, trust_badges: arr } };
                    })} className="flex-1" />
                    <Button variant="ghost" size="icon" onClick={() => setDraft(d => {
                      const arr = [...(d.hero?.trust_badges || [])];
                      arr.splice(i, 1);
                      return { ...d, hero: { ...d.hero, trust_badges: arr } };
                    })}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="gap-1" onClick={() => setDraft(d => ({
                  ...d, hero: { ...d.hero, trust_badges: [...(d.hero?.trust_badges || []), { icon: 'sparkles', label: '' }] },
                }))}><Plus className="h-3 w-3" /> Add trust badge</Button>
              </div>
            </div>

            <div className="border-t pt-3">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Floating card (over the image)</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Input placeholder="Eyebrow (small)" value={draft?.hero?.floating_card?.eyebrow || ''} onChange={e => setDraft(d => ({
                  ...d, hero: { ...d.hero, floating_card: { ...(d.hero?.floating_card || {}), eyebrow: e.target.value } },
                }))} />
                <Input placeholder="Label" value={draft?.hero?.floating_card?.label || ''} onChange={e => setDraft(d => ({
                  ...d, hero: { ...d.hero, floating_card: { ...(d.hero?.floating_card || {}), label: e.target.value } },
                }))} />
              </div>
            </div>
          </Section>

          {/* ─── Value props ─── */}
          <Section title="Value Props (4 cards)">
            <HeadingFields h={draft?.value_props_heading} onChange={(k, v) => patchNested('value_props_heading', k, v)} />
            <div className="space-y-2 mt-2">
              {(draft?.value_props || []).map((it, i) => (
                <ItemCard
                  key={i}
                  onRemove={() => removeArrayItem('value_props', i)}
                  onUp={() => moveArrayItem('value_props', i, -1)}
                  onDown={() => moveArrayItem('value_props', i, +1)}
                >
                  <div className="flex gap-2">
                    <IconSelect value={it.icon} onChange={(v) => patchArrayItem('value_props', i, 'icon', v)} />
                    <Input value={it.title || ''} placeholder="Title" onChange={e => patchArrayItem('value_props', i, 'title', e.target.value)} className="flex-1" />
                  </div>
                  <Textarea rows={2} value={it.body || ''} placeholder="Body" onChange={e => patchArrayItem('value_props', i, 'body', e.target.value)} />
                </ItemCard>
              ))}
              <Button variant="outline" size="sm" onClick={() => addArrayItem('value_props', { icon: 'sparkles', title: '', body: '' })} className="gap-1"><Plus className="h-3 w-3" />Add card</Button>
            </div>
          </Section>

          {/* ─── Featured Homes ─── */}
          <Section title="Featured Homes">
            <HeadingFields h={draft?.homes_heading} onChange={(k, v) => patchNested('homes_heading', k, v)} />
            <div className="grid grid-cols-2 gap-2">
              <Field label="Strategy">
                <Select value={draft?.homes_settings?.strategy || 'one_per_building'} onValueChange={(v) => patchHomesSettings({ strategy: v })}>
                  <SelectTrigger data-testid="editor-homes-strategy"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_per_building">One unit per building (auto)</SelectItem>
                    <SelectItem value="manual">Manual picks</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Limit">
                <Input type="number" min={1} max={12} value={draft?.homes_settings?.limit || 3} onChange={e => patchHomesSettings({ limit: Number(e.target.value) || 3 })} />
              </Field>
            </div>

            {draft?.homes_settings?.strategy === 'one_per_building' && (
              <div className="border-t pt-3 space-y-2">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Per-building rules</p>
                {inventory.length === 0 && <p className="text-xs text-muted-foreground italic">Loading buildings…</p>}
                {inventory.map(prop => {
                  const excluded = (draft?.homes_settings?.excluded_property_ids || []).includes(prop.id);
                  const preferred = draft?.homes_settings?.preferred_unit_by_property?.[prop.id] || '';
                  return (
                    <Card key={prop.id} className="p-3">
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!excluded}
                            onChange={e => {
                              const currentExcluded = new Set(draft?.homes_settings?.excluded_property_ids || []);
                              if (!e.target.checked) currentExcluded.add(prop.id);
                              else currentExcluded.delete(prop.id);
                              patchHomesSettings({ excluded_property_ids: Array.from(currentExcluded) });
                            }}
                            data-testid={`editor-homes-include-${prop.id}`}
                          />
                          <span className="text-sm font-medium truncate">{prop.name}</span>
                          <span className="text-[10px] text-muted-foreground">{prop.units.length} unit{prop.units.length === 1 ? '' : 's'}</span>
                        </label>
                      </div>
                      {!excluded && prop.units.length > 0 && (
                        <div className="mt-2 pl-6">
                          <Select value={preferred || 'auto'} onValueChange={(v) => {
                            const map = { ...(draft?.homes_settings?.preferred_unit_by_property || {}) };
                            if (v === 'auto') delete map[prop.id];
                            else map[prop.id] = v;
                            patchHomesSettings({ preferred_unit_by_property: map });
                          }}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`editor-homes-unit-${prop.id}`}>
                              <SelectValue placeholder="Auto-pick first listed unit" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="auto">Auto-pick first listed unit</SelectItem>
                              {prop.units.map(u => (
                                <SelectItem key={u.id} value={u.id}>
                                  {u.name} {!u.is_listed && '(unlisted)'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}

            {draft?.homes_settings?.strategy === 'manual' && (
              <div className="border-t pt-3 space-y-2">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Pick specific units in order</p>
                {inventory.flatMap(p => p.units.map(u => ({ p, u }))).map(({ p, u }) => {
                  const checked = (draft?.homes_settings?.manual_unit_ids || []).includes(u.id);
                  return (
                    <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer px-2 py-1 hover:bg-muted/40 rounded">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => {
                          const arr = [...(draft?.homes_settings?.manual_unit_ids || [])];
                          if (e.target.checked) arr.push(u.id);
                          else {
                            const idx = arr.indexOf(u.id);
                            if (idx >= 0) arr.splice(idx, 1);
                          }
                          patchHomesSettings({ manual_unit_ids: arr });
                        }}
                      />
                      <span className="flex-1 truncate">
                        <span className="text-muted-foreground text-[10px] uppercase mr-1">{p.name}</span>{u.name}
                      </span>
                      {checked && <Check className="h-3 w-3 text-[#4E6A52]" />}
                    </label>
                  );
                })}
              </div>
            )}
          </Section>

          {/* ─── Audiences ─── */}
          <Section title="Who It's For">
            <HeadingFields h={draft?.audiences_heading} onChange={(k, v) => patchNested('audiences_heading', k, v)} />
            <div className="space-y-2 mt-2">
              {(draft?.audiences || []).map((it, i) => (
                <ItemCard
                  key={i}
                  onRemove={() => removeArrayItem('audiences', i)}
                  onUp={() => moveArrayItem('audiences', i, -1)}
                  onDown={() => moveArrayItem('audiences', i, +1)}
                >
                  <div className="flex gap-2">
                    <Input value={it.emoji || ''} maxLength={4} placeholder="🏥" className="w-16 text-center" onChange={e => patchArrayItem('audiences', i, 'emoji', e.target.value)} />
                    <Input value={it.title || ''} placeholder="Title" onChange={e => patchArrayItem('audiences', i, 'title', e.target.value)} className="flex-1" />
                  </div>
                  <Textarea rows={2} value={it.body || ''} placeholder="Body" onChange={e => patchArrayItem('audiences', i, 'body', e.target.value)} />
                </ItemCard>
              ))}
              <Button variant="outline" size="sm" onClick={() => addArrayItem('audiences', { emoji: '✨', title: '', body: '' })} className="gap-1"><Plus className="h-3 w-3" />Add audience</Button>
            </div>
          </Section>

          {/* ─── How it works ─── */}
          <Section title="How It Works (3 steps)">
            <HeadingFields h={draft?.how_it_works_heading} onChange={(k, v) => patchNested('how_it_works_heading', k, v)} />
            <div className="space-y-2 mt-2">
              {(draft?.how_it_works || []).map((it, i) => (
                <ItemCard
                  key={i}
                  onRemove={() => removeArrayItem('how_it_works', i)}
                  onUp={() => moveArrayItem('how_it_works', i, -1)}
                  onDown={() => moveArrayItem('how_it_works', i, +1)}
                >
                  <div className="flex gap-2">
                    <Input value={it.n || ''} placeholder="01" className="w-20" onChange={e => patchArrayItem('how_it_works', i, 'n', e.target.value)} />
                    <Input value={it.title || ''} placeholder="Title" onChange={e => patchArrayItem('how_it_works', i, 'title', e.target.value)} className="flex-1" />
                  </div>
                  <Textarea rows={2} value={it.body || ''} placeholder="Body" onChange={e => patchArrayItem('how_it_works', i, 'body', e.target.value)} />
                </ItemCard>
              ))}
              <Button variant="outline" size="sm" onClick={() => addArrayItem('how_it_works', { n: '', title: '', body: '' })} className="gap-1"><Plus className="h-3 w-3" />Add step</Button>
            </div>
          </Section>

          {/* ─── Testimonials ─── */}
          <Section title="Testimonials">
            <div className="space-y-2">
              {(draft?.testimonials || []).map((it, i) => (
                <ItemCard
                  key={i}
                  onRemove={() => removeArrayItem('testimonials', i)}
                  onUp={() => moveArrayItem('testimonials', i, -1)}
                  onDown={() => moveArrayItem('testimonials', i, +1)}
                >
                  <Textarea rows={3} value={it.quote || ''} placeholder="Quote" onChange={e => patchArrayItem('testimonials', i, 'quote', e.target.value)} />
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={it.name || ''} placeholder="Name" onChange={e => patchArrayItem('testimonials', i, 'name', e.target.value)} />
                    <Input value={it.role || ''} placeholder="Role / context" onChange={e => patchArrayItem('testimonials', i, 'role', e.target.value)} />
                  </div>
                </ItemCard>
              ))}
              <Button variant="outline" size="sm" onClick={() => addArrayItem('testimonials', { quote: '', name: '', role: '' })} className="gap-1"><Plus className="h-3 w-3" />Add testimonial</Button>
            </div>
          </Section>

          {/* ─── Final CTA ─── */}
          <Section title="Final CTA banner">
            <Field label="Title">
              <Input value={draft?.final_cta?.title || ''} onChange={e => patchNested('final_cta', 'title', e.target.value)} />
            </Field>
            <Field label="Subtitle">
              <Textarea rows={2} value={draft?.final_cta?.subtitle || ''} onChange={e => patchNested('final_cta', 'subtitle', e.target.value)} />
            </Field>
            <Field label="Button label">
              <Input value={draft?.final_cta?.button_label || ''} onChange={e => patchNested('final_cta', 'button_label', e.target.value)} />
            </Field>
          </Section>

          {/* ─── Footer ─── */}
          <Section title="Footer">
            <Field label="Tagline">
              <Textarea rows={2} value={draft?.footer?.tagline || ''} onChange={e => patchNested('footer', 'tagline', e.target.value)} />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Contact email">
                <Input type="email" value={draft?.footer?.contact_email || ''} onChange={e => patchNested('footer', 'contact_email', e.target.value)} />
              </Field>
              <Field label="Location">
                <Input value={draft?.footer?.location || ''} onChange={e => patchNested('footer', 'location', e.target.value)} />
              </Field>
            </div>
            <Field label="Copyright suffix">
              <Input value={draft?.footer?.copyright_suffix || ''} onChange={e => patchNested('footer', 'copyright_suffix', e.target.value)} />
            </Field>
          </Section>
        </div>

        <div className="px-5 py-3 border-t flex items-center justify-end gap-2 flex-shrink-0 bg-background">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="gap-1.5 bg-[#4E6A52] hover:bg-[#3d5541]" data-testid="editor-save">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save changes
          </Button>
        </div>

        {showPhotoPicker && (
          <div
            className="absolute inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowPhotoPicker(false)}
            data-testid="editor-photo-picker"
          >
            <div
              className="bg-background rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-3 border-b flex items-center justify-between">
                <h3 className="font-medium">Pick photos from listings</h3>
                <button onClick={() => setShowPhotoPicker(false)} className="text-muted-foreground hover:text-foreground">✕</button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {photoLibrary.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic text-center py-12">
                    No listing photos yet. Add photos to any listing on the Listings page and they&rsquo;ll show up here.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {photoLibrary.map((p, i) => {
                      const alreadyIn = (draft?.hero?.images || []).includes(p.url);
                      const displayUrl = p.url && !p.url.startsWith('http') ? `${API}${p.url}` : p.url;
                      return (
                        <button
                          key={p.url + i}
                          onClick={() => {
                            setDraft(d => {
                              const arr = d.hero?.images || [];
                              const next = alreadyIn ? arr.filter(u => u !== p.url) : [...arr, p.url];
                              return { ...d, hero: { ...d.hero, images: next } };
                            });
                          }}
                          className={`relative rounded-lg overflow-hidden aspect-[4/3] border-2 transition-all ${alreadyIn ? 'border-[#4E6A52] ring-2 ring-[#4E6A52]/20' : 'border-transparent hover:border-[#4E6A52]/40'}`}
                          data-testid={`editor-photo-pick-${i}`}
                        >
                          <img src={displayUrl} alt={p.unit_name} className="h-full w-full object-cover" loading="lazy" />
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                            <p className="text-[10px] text-white font-medium truncate">{p.property_name}</p>
                            <p className="text-[9px] text-white/80 truncate">{p.unit_name}</p>
                          </div>
                          {alreadyIn && (
                            <div className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full bg-[#4E6A52] flex items-center justify-center">
                              <Check className="h-3 w-3 text-white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="px-5 py-3 border-t flex items-center justify-between bg-muted/20">
                <p className="text-xs text-muted-foreground">
                  {(draft?.hero?.images || []).length} image{(draft?.hero?.images || []).length === 1 ? '' : 's'} selected
                </p>
                <Button size="sm" onClick={() => setShowPhotoPicker(false)}>Done</Button>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full px-3 py-2 text-left text-sm font-medium bg-muted/40 hover:bg-muted/70 flex items-center justify-between"
      >
        <span>{title}</span>
        <span className="text-xs text-muted-foreground">{open ? '▾' : '▸'}</span>
      </button>
      {open && <div className="p-3 space-y-3">{children}</div>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function HeadingFields({ h, onChange }) {
  return (
    <div className="grid grid-cols-1 gap-2">
      <div className="grid grid-cols-[120px_1fr] gap-2">
        <Input placeholder="Eyebrow" value={h?.eyebrow || ''} onChange={e => onChange('eyebrow', e.target.value)} />
        <Input placeholder="Section title" value={h?.title || ''} onChange={e => onChange('title', e.target.value)} />
      </div>
    </div>
  );
}

function IconSelect({ value, onChange }) {
  return (
    <Select value={value || 'sparkles'} onValueChange={onChange}>
      <SelectTrigger className="w-28 h-9 text-xs"><SelectValue /></SelectTrigger>
      <SelectContent>
        {ICON_CHOICES.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function ItemCard({ children, onRemove, onUp, onDown }) {
  return (
    <Card className="p-3 space-y-2 relative group">
      <div className="absolute top-1 right-1 flex opacity-0 group-hover:opacity-100 transition-opacity">
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onUp}><span className="text-xs">↑</span></Button>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onDown}><span className="text-xs">↓</span></Button>
        <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={onRemove}><Trash2 className="h-3 w-3" /></Button>
      </div>
      {children}
    </Card>
  );
}
